/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from "@angular/core";
import {MatIconRegistry} from "@angular/material/icon";
import {DomSanitizer} from "@angular/platform-browser";
import {SwUpdate} from "@angular/service-worker";
import {
  SIDE_MENU_MAIN,
  SIDE_MENU_NOTIFICATION_CENTER,
  SIDE_MENU_VOICE_ASSISTANT
} from "@app/models/constants/side-menu.constants";
import {CommonActions} from "@app/store/common/common.actions";
import {CommonState} from "@app/store/common/common.state";
import {environment} from "@env/environment";
import {MenuController} from "@ionic/angular";
import {ApplicationInsights} from "@microsoft/applicationinsights-web";
import {Select, Store} from "@ngxs/store";
import {TrecEnvironmentService, TrecSitesService} from "@trec/api/utils";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {Observable, Subscription} from "rxjs";
import {pairwise} from "rxjs/operators";
import {AppEnvironmentConfig} from "./app.environment.config";
import {PushManagerService} from "./modules/base/services/push-manager.service";

@Component({
  selector: "c3-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  @Select(CommonState.isAuthenticated) isAuthenticated$: Observable<boolean>;
  readonly SIDE_MENU_NOTIFICATION_CENTER = SIDE_MENU_NOTIFICATION_CENTER;
  readonly SIDE_MENU_MAIN = SIDE_MENU_MAIN;
  readonly SIDE_MENU_VOICE_ASSISTANT = SIDE_MENU_VOICE_ASSISTANT;

  private readonly subscr = new Subscription();

  // Added: boolean flag indicating presence of configuratoreAssistant
  public hasConfiguratoreAssistant = false;
  public isAuthenticated = false;

  constructor(
    private store: Store,
    private trecEnvironment: TrecEnvironmentService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private swUpdate: SwUpdate,
    private trecSites: TrecSitesService,
    private snackbarStatus: C3SnackbarStatusService,
    public viewContainerRef: ViewContainerRef, // Used by other services
    private pushManager: PushManagerService,
    private menuCtrl: MenuController
  ) { }

  async ngOnInit() {
    const appInsights = new ApplicationInsights({
      config: {
        connectionString: environment.connectionString,
      },
    });
    appInsights.loadAppInsights();
    appInsights.trackPageView();

    document.documentElement.style.setProperty(
      "--ion-color-primary",
      AppEnvironmentConfig.primaryColor
    );

    // Print webapp version
    this.trecEnvironment.printLogo(environment.version);

    this.initIcons();
    this.initPwa();
    this.initTokenListener();
    // this.initRouterScrollToTop();

    // Load TreC available service/feature list
    this.store.dispatch(new CommonActions.InitApp());

    // First thing when app loads -> load token into store
    this.store.dispatch(new CommonActions.LoadToken());

    // Initialize boolean flag from localStorage (safe parsing)
    // Soluzione più semplice e diretta
    try {
      const raw = localStorage.getItem("configuratoreAssistant");
      this.hasConfiguratoreAssistant = !!(raw && raw !== "null" && raw !== "undefined" && JSON.parse(raw));
    } catch {
      this.hasConfiguratoreAssistant = false;
    }
    this.isAuthenticated$.subscribe(value => this.isAuthenticated = value);
    // Initialize push notifications
    this.pushManager.initFirebaseMessaging();
  }

  private initIcons() {
    // register mdi icons
    this.matIconRegistry.addSvgIconSet(
      this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/fonts/mdi.svg")
    );
  }

  private async initPwa() {
    // Reload page if any update is find
    if (this.swUpdate.isEnabled) {
      this.subscr.add(
        this.swUpdate.versionUpdates.subscribe((evt) => {
          switch (evt.type) {
            case "VERSION_DETECTED":
              // On new update -> ask to reload page
              this.snackbarStatus.showInfo({
                message:
                  "È disponibile una versione più recente del sito. Per aggiornare è sufficiente ricaricare la pagina.",
                buttons: [
                  {
                    text: "Ricarica pagina",
                    side: "end",
                    handler: () => {
                      this.swUpdate.activateUpdate().then(() => document.location.reload());
                    },
                  },
                  {
                    role: "cancel",
                    side: "end",
                    icon: "close-outline",
                  },
                ],
                duration: null,
              });

              // On new update -> immediately reload the page
              // this.swUpdate.activateUpdate().then(() => document.location.reload());

              break;
            case "VERSION_READY":
              // New version activated -> inform user
              this.snackbarStatus.showInfo({
                message: "Il sito è stato aggiornato",
                duration: 3000,
              });
              break;
            case "VERSION_INSTALLATION_FAILED":
              console.error(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
              break;
          }
        })
      );
    }
  }

  /*   private async initPwa() {
    // Reload page if any update is find
    if (this.swUpdate.isEnabled) {
      this.subscr.add(
        this.swUpdate.available.subscribe(() => {
          // On new update -> ask to reload page
          this.snackbarStatus.showInfo({
            message:
              "È disponibile una versione più recente del sito. Per aggiornare è sufficiente ricaricare la pagina.",
            buttons: [
              {text: "Chiudi", role: "cancel", side: "start"},
              {
                text: "Ricarica",
                side: "end",
                handler: () => {
                  this.swUpdate.activateUpdate().then(() => document.location.reload());
                },
              },
            ],
            duration: null,
          });

          // On new update -> immediately reload the page
          // this.swUpdate.activateUpdate().then(() => document.location.reload());
        })
      );

      this.subscr.add(
        await this.swUpdate.activateUpdate().then((activated) => {
          // New version activated -> inform user
          this.snackbarStatus.showInfo({message: "Il sito è stato aggiornato", duration: 3000});
        })
      );
    }
  } */

  private initTokenListener() {
    // Listen for token -> if token changes from "set" to "undefined/missing" -> go to login page
    this.subscr.add(
      this.store
        .select(CommonState.trecToken)
        .pipe(pairwise())
        .subscribe(([prev, curr]) => {
          // curr -> current token value
          // prev -> previous token value
          if (curr === null && prev !== "notset" && prev !== null) {
            // Token changed from "set" to "missing" -> go to login page
            this.trecSites.goToAuthenticationPage();
          }
        })
    );
  }

  /**
   * Scroll to top the drawer-content tag's "content" every time route is changed ("Scroll" router event is triggered).
   */
  // private initRouterScrollToTop() {
  //   this.router.events.pipe(
  //     filter(e => e instanceof Scroll)
  //   ).subscribe(() => {
  //     if (this.drawerContent.nativeElement.scrollTo) {
  //       this.drawerContent.nativeElement.scrollTo(0, 0);
  //     }
  //   });
  // }

  async onMainMenuClose() {
    // Once the main menu is closed, since there are multiple "left" menus, all the others must be re-enabled
    // They have been disabled when the main menu opened

    const menus = await this.menuCtrl.getMenus();

    for (const menu of menus) {
      this.menuCtrl.enable(true, menu.menuId);
    }
  }

  ngOnDestroy() {
    this.subscr.unsubscribe();

    // Shutdown FCM
    this.pushManager.shutdownFirebaseMessaging();
  }
}
